export const riskManagementCourse = {
  id: "risk-management-derivatives-2024",
  title: "Risk Management and Derivatives: Complete Professional Course",
  shortDescription: "Master enterprise risk management, derivatives strategies, and advanced risk assessment techniques used by financial institutions and corporations.",
  description: "Comprehensive course covering market risk, credit risk, operational risk, and derivatives applications for hedging and risk management in modern finance.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "advanced",
  duration: 21,
  estimatedWeeks: 11,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from risk managers and derivatives professionals",
  thumbnailUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Identify, measure, and manage different types of financial risks",
    "Apply derivatives for hedging and risk management strategies",
    "Implement Value at Risk (VaR) and stress testing techniques",
    "Understand credit risk modeling and assessment",
    "Master operational risk management frameworks",
    "Apply portfolio risk management and optimization",
    "Understand regulatory risk management requirements",
    "Develop comprehensive risk management policies"
  ],

  prerequisites: [
    "Financial Markets Knowledge",
    "Statistics and Probability",
    "Derivatives Fundamentals",
    "Excel/Programming Skills"
  ],

  skills: [
    "Risk Assessment",
    "Derivatives Trading",
    "VaR Modeling",
    "Credit Analysis",
    "Portfolio Risk Management",
    "Stress Testing",
    "Regulatory Compliance",
    "Risk Policy Development"
  ],

  tags: [
    "Risk Management",
    "Derivatives",
    "VaR Modeling",
    "Credit Risk",
    "Market Risk",
    "Operational Risk",
    "Hedging Strategies",
    "Financial Regulation"
  ],

  marketData: {
    averageSalary: 135000,
    salaryRange: {
      min: 85000,
      max: 220000
    },
    jobDemand: 4.6,
    marketTrend: "growing",
    careerPaths: [
      "Risk Manager",
      "Derivatives Trader",
      "Credit Risk Analyst",
      "Market Risk Analyst",
      "Compliance Officer",
      "Chief Risk Officer"
    ],
    relatedSkills: [
      "Bloomberg Terminal",
      "Python/R",
      "Risk Management Systems",
      "Regulatory Knowledge",
      "Financial Modeling",
      "Statistics"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "risk-fundamentals",
      title: "Risk Management Fundamentals",
      description: "Types of risk, risk measurement, and risk management frameworks",
      order: 1,
      estimatedHours: 4.5,
      content: [
        {
          id: "risk-types",
          title: "Types of Financial Risk",
          description: "Market risk, credit risk, liquidity risk, and operational risk",
          type: "video",
          duration: 40
        },
        {
          id: "risk-assessment-tool",
          title: "Risk Assessment and Measurement",
          description: "Interactive tool for measuring and analyzing different types of risk",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "risk-assessment",
            instructions: "Assess various risk exposures for a financial institution and calculate key risk metrics including VaR and expected shortfall.",
            scenario: {
              portfolio: {
                stocks: 5000000,
                bonds: 3000000,
                derivatives: 1000000,
                cash: 1000000
              },
              riskFactors: {
                stockVolatility: 0.20,
                bondDuration: 5.0,
                fxExposure: 2000000,
                creditSpread: 0.02
              }
            },
            hints: [
              "VaR measures potential loss at a given confidence level",
              "Consider correlations between different risk factors",
              "Stress test the portfolio under extreme scenarios",
              "Expected shortfall measures tail risk beyond VaR"
            ]
          }
        },
        {
          id: "advanced-risk-measures",
          title: "Advanced Risk Measures and Models",
          description: "Expected Shortfall, coherent risk measures, and advanced risk modeling techniques",
          type: "text",
          duration: 55,
          textContent: `# Advanced Risk Measures and Models

## Beyond Value at Risk

### Limitations of VaR

**Lack of Subadditivity:**
- **Portfolio VaR ≠ Sum of Individual VaRs**: VaR may not capture diversification benefits properly
- **Risk Concentration**: May underestimate risk of concentrated positions
- **Tail Risk Ignorance**: VaR doesn't consider losses beyond the confidence level
- **Model Risk**: Sensitive to distributional assumptions

**Expected Shortfall (ES) / Conditional VaR:**
- **Definition**: Expected loss given that loss exceeds VaR threshold
- **Formula**: ES_α = E[L | L > VaR_α]
- **Coherence**: Satisfies all coherent risk measure properties
- **Tail Risk**: Captures information about extreme losses

**Spectral Risk Measures:**
- **Weighted Averages**: Different weights for different quantiles
- **Risk Aversion**: Incorporates investor risk preferences
- **Customization**: Flexible framework for different applications
- **Examples**: Exponential spectral measure, power spectral measure

### Coherent Risk Measures

**Artzner et al. Properties:**

**1. Monotonicity:**
- If X ≤ Y (in loss terms), then ρ(X) ≤ ρ(Y)
- Larger losses should have larger risk measures

**2. Translation Invariance:**
- ρ(X + c) = ρ(X) + c for constant c
- Adding cash reduces risk by the same amount

**3. Positive Homogeneity:**
- ρ(λX) = λρ(X) for λ > 0
- Scaling position scales risk proportionally

**4. Subadditivity:**
- ρ(X + Y) ≤ ρ(X) + ρ(Y)
- Diversification never increases risk

**Implications for Risk Management:**
- **Capital Allocation**: Coherent measures enable proper risk-based capital allocation
- **Portfolio Optimization**: Meaningful risk budgeting and optimization
- **Regulatory Acceptance**: Increasingly preferred by regulators

## Advanced Volatility Models

### GARCH Model Extensions

**Threshold GARCH (T-GARCH):**
- **Asymmetric Effects**: Different volatility responses to positive/negative shocks
- **Specification**: σ²t = ω + αε²t-1 + γε²t-1I[εt-1<0] + βσ²t-1
- **Leverage Effect**: Negative shocks increase volatility more than positive shocks
- **Applications**: Equity volatility modeling, risk management

**Exponential GARCH (E-GARCH):**
- **Log Specification**: ln(σ²t) = ω + α|εt-1/σt-1| + γεt-1/σt-1 + β ln(σ²t-1)
- **No Restrictions**: Parameters can be negative
- **Asymmetry**: Captures leverage effects naturally
- **Persistence**: Long memory in volatility

**Fractionally Integrated GARCH (FI-GARCH):**
- **Long Memory**: Persistent volatility clustering
- **Fractional Integration**: 0 < d < 1 parameter
- **Slow Decay**: Volatility shocks decay hyperbolically
- **Applications**: High-frequency data, long-term risk models

### Multivariate Volatility Models

**Dynamic Conditional Correlation (DCC):**
- **Time-Varying Correlations**: Correlations evolve over time
- **Specification**: Rt = Qt^(-1/2) Qt Qt^(-1/2)
- **Estimation**: Two-step approach (GARCH then correlations)
- **Applications**: Portfolio risk management, asset allocation

**BEKK Model:**
- **Bivariate Extension**: Natural generalization of univariate GARCH
- **Positive Definiteness**: Ensures valid covariance matrices
- **Parameter Restrictions**: Many parameters, potential overparameterization
- **Estimation**: Maximum likelihood, computationally intensive

**Factor Models:**
- **Common Factors**: Reduce dimensionality of covariance estimation
- **Orthogonal Factors**: Principal components, factor analysis
- **Economic Factors**: Interest rates, market indices, currencies
- **Applications**: Large portfolio risk management

## Extreme Value Theory

### Statistical Foundation

**Generalized Extreme Value (GEV) Distribution:**
- **Block Maxima**: Maximum values over fixed time periods
- **Three Types**: Gumbel (ξ=0), Fréchet (ξ>0), Weibull (ξ<0)
- **Parameters**: Location (μ), scale (σ), shape (ξ)
- **Applications**: Flood modeling, insurance, operational risk

**Generalized Pareto Distribution (GPD):**
- **Threshold Exceedances**: Values above high threshold
- **Parameters**: Scale (σ), shape (ξ)
- **Tail Behavior**: Heavy tails when ξ > 0
- **Applications**: VaR calculation, stress testing

### Peaks Over Threshold Method

**Threshold Selection:**
- **Mean Excess Plot**: Plot mean excess vs. threshold
- **Hill Estimator**: Tail index estimation
- **Goodness of Fit**: QQ plots, Kolmogorov-Smirnov tests
- **Stability**: Robust threshold choice

**Risk Measure Estimation:**
- **High Quantile VaR**: VaR_p = u + (σ/ξ)[(n/Nu × (1-p))^(-ξ) - 1]
- **Expected Shortfall**: ES_p = VaR_p/(1-ξ) + (σ-ξu)/(1-ξ)
- **Confidence Intervals**: Bootstrap or asymptotic standard errors

### Applications in Finance

**Tail Risk Management:**
- **Extreme VaR**: 99.9%, 99.95% confidence levels
- **Stress Testing**: Scenario analysis using extreme value distributions
- **Capital Allocation**: Tail risk-based capital requirements
- **Insurance Modeling**: Catastrophic loss modeling

**Operational Risk:**
- **Loss Distribution Approach**: EVT for severity distributions
- **Basel III**: Regulatory capital for operational risk
- **Scenario Analysis**: Extreme operational loss events
- **Business Line Analysis**: Tail risk by business unit

## Credit Risk Models

### Structural Models

**Merton Model:**
- **Asset Value Process**: V_t = V_0 exp[(r-σ²/2)t + σW_t]
- **Default Trigger**: Default when V_T < D (debt value)
- **Default Probability**: N(-d2) where d2 from Black-Scholes formula
- **Recovery Rate**: (V_T - D)/D if V_T > D, 0 otherwise

**Extensions:**
- **First Passage Time**: Default when asset value first hits barrier
- **Stochastic Interest Rates**: Time-varying risk-free rates
- **Jump Diffusions**: Sudden changes in asset values
- **Endogenous Default**: Optimal default timing

### Reduced Form Models

**Hazard Rate Models:**
- **Default Intensity**: λ(t) = instantaneous default probability
- **Survival Probability**: S(t) = exp(-∫₀ᵗ λ(s)ds)
- **Default Probability**: 1 - S(t)
- **Calibration**: Credit spreads, CDS prices

**Jarrow-Turnbull Model:**
- **State-Dependent Intensity**: λ(t,X_t) depends on state variables
- **Economic Factors**: Interest rates, equity indices, macro variables
- **Recovery Modeling**: Stochastic recovery rates
- **Applications**: Credit derivatives pricing, portfolio credit risk

### Portfolio Credit Risk

**Gaussian Copula Model:**
- **Asset Correlation**: Correlated standard normal variables
- **Threshold Model**: Default when asset value < threshold
- **Factor Structure**: Systematic and idiosyncratic risk factors
- **Applications**: CDO pricing, economic capital models

**CreditMetrics:**
- **Rating Transitions**: Migration matrices for credit ratings
- **Monte Carlo**: Simulation of rating changes and defaults
- **Correlations**: Asset correlations via equity market data
- **Portfolio Loss**: Distribution of credit losses

**KMV Portfolio Manager:**
- **Distance to Default**: Merton model-based default probabilities
- **Asset Correlations**: Estimated from equity return correlations
- **Expected Default Frequency**: Physical default probabilities
- **Economic Capital**: Unexpected loss at high confidence levels

## Market Risk Models

### Factor Models

**Principal Component Analysis:**
- **Dimensionality Reduction**: Capture most variance with fewer factors
- **Interpretation**: Economic meaning of principal components
- **Yield Curve**: Level, slope, curvature factors
- **Equity Markets**: Market, size, value factors

**Arbitrage Pricing Theory (APT):**
- **Multi-Factor Model**: R_i = α_i + Σ β_{i,j} F_j + ε_i
- **Factor Selection**: Macroeconomic, fundamental, statistical factors
- **Risk Decomposition**: Systematic vs. specific risk
- **Applications**: Risk budgeting, performance attribution

**Fama-French Models:**
- **Three-Factor**: Market, size (SMB), value (HML)
- **Five-Factor**: Add profitability (RMW), investment (CMA)
- **Six-Factor**: Add momentum factor
- **Applications**: Expected return estimation, risk adjustment

### Options Risk Management

**Greeks and Risk Sensitivities:**

**Delta (Δ):**
- **Definition**: ∂V/∂S (price sensitivity to underlying)
- **Hedging**: Delta-neutral portfolios
- **Dynamic**: Changes with underlying price and time
- **Portfolio Delta**: Sum of position deltas

**Gamma (Γ):**
- **Definition**: ∂²V/∂S² (convexity of option value)
- **Risk**: Large gamma means unstable delta hedges
- **Trading**: Gamma scalping strategies
- **Time Decay**: Gamma highest near expiration at-the-money

**Vega (ν):**
- **Definition**: ∂V/∂σ (volatility sensitivity)
- **Risk**: Volatility risk in option positions
- **Term Structure**: Different vegas for different maturities
- **Hedging**: Vega-neutral portfolios

**Theta (Θ):**
- **Definition**: ∂V/∂t (time decay)
- **Risk**: Passage of time effect on option values
- **Acceleration**: Non-linear time decay near expiration
- **Income**: Theta collection strategies

**Rho (ρ):**
- **Definition**: ∂V/∂r (interest rate sensitivity)
- **Importance**: More significant for longer-term options
- **Currency Options**: Dual sensitivity to domestic and foreign rates

Advanced risk modeling requires sophisticated mathematical techniques and careful attention to model assumptions and limitations.`
        }
      ],
      quiz: {
        id: "risk-fundamentals-quiz",
        title: "Risk Management Fundamentals Quiz",
        description: "Test your understanding of risk types and measurement",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does Value at Risk (VaR) measure?",
            options: [
              "The expected return of a portfolio",
              "The maximum loss over a time period at a given confidence level",
              "The volatility of portfolio returns",
              "The correlation between portfolio components"
            ],
            correctAnswer: 1,
            explanation: "VaR measures the maximum expected loss over a specific time period at a given confidence level (e.g., 95% or 99%)."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main advantage of Expected Shortfall over VaR?",
            options: [
              "It's easier to calculate",
              "It captures tail risk beyond the VaR threshold",
              "It requires fewer assumptions",
              "It's more widely used in practice"
            ],
            correctAnswer: 1,
            explanation: "Expected Shortfall captures information about the tail of the loss distribution beyond the VaR threshold, providing a more complete picture of tail risk."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "In the Merton credit risk model, default occurs when:",
            options: [
              "The stock price falls below zero",
              "The firm's asset value falls below its debt value",
              "Interest rates rise above a threshold",
              "Volatility exceeds a certain level"
            ],
            correctAnswer: 1,
            explanation: "In the Merton model, default occurs when the firm's asset value falls below the value of its debt at maturity, representing insolvency."
          }
        ]
      }
    },
    {
      id: "derivatives-hedging",
      title: "Derivatives and Hedging Strategies",
      description: "Using derivatives for risk management and hedging applications",
      order: 2,
      estimatedHours: 5.0,
      content: [
        {
          id: "hedging-basics",
          title: "Hedging Fundamentals",
          description: "Basic hedging concepts and strategies using derivatives",
          type: "video",
          duration: 45
        },
        {
          id: "comprehensive-hedging",
          title: "Comprehensive Hedging Strategies",
          description: "Advanced hedging techniques using various derivative instruments",
          type: "text",
          duration: 65,
          textContent: `# Comprehensive Hedging Strategies

## Hedging Fundamentals

### Basic Hedging Principles

**Perfect Hedge vs. Imperfect Hedge:**
- **Perfect Hedge**: Completely eliminates risk (theoretical)
- **Imperfect Hedge**: Reduces but doesn't eliminate risk (practical reality)
- **Basis Risk**: Risk from imperfect correlation between hedge and underlying
- **Cross-Hedging**: Using related but different instruments for hedging

**Static vs. Dynamic Hedging:**
- **Static Hedge**: Set once and held to maturity
- **Dynamic Hedge**: Continuously adjusted as conditions change
- **Delta Hedging**: Maintaining delta-neutral positions
- **Rebalancing Frequency**: Trade-off between accuracy and transaction costs

**Hedge Effectiveness:**
- **Measurement**: Correlation between hedge gains/losses and underlying losses/gains
- **Accounting Standards**: ASC 815 (US GAAP), IFRS 9 hedge accounting
- **Testing**: Prospective and retrospective effectiveness tests
- **Documentation**: Formal hedge documentation requirements

### Hedge Ratio Determination

**Minimum Variance Hedge Ratio:**
h* = Cov(ΔS, Δf) / Var(Δf)

Where:
- ΔS = change in spot price
- Δf = change in futures price
- h* = optimal hedge ratio

**OLS Regression Method:**
ΔS_t = α + β Δf_t + ε_t

Hedge ratio = β coefficient

**Error Correction Models:**
- **Cointegration**: Long-run relationship between spot and futures
- **Short-run Dynamics**: Adjustment to equilibrium
- **VECM**: Vector error correction model for hedge ratios

## Currency Risk Hedging

### Transaction Exposure Hedging

**Forward Contracts:**
- **Mechanics**: Lock in exchange rate for future transaction
- **Pricing**: F = S × (1 + r_d)/(1 + r_f) × T
- **Advantages**: Perfect hedge for known exposures, no upfront cost
- **Disadvantages**: Obligation to transact, opportunity cost

**Currency Options:**
- **Call Options**: Right to buy foreign currency
- **Put Options**: Right to sell foreign currency
- **Premium Cost**: Upfront payment required
- **Asymmetric Payoff**: Protection while retaining upside potential

**Money Market Hedging:**
- **Borrow/Lend**: Create synthetic forward rate
- **Example**: EUR receivable → Borrow EUR, convert to USD, invest USD
- **Comparison**: Should yield same result as forward contract
- **Credit Risk**: Counterparty risk in lending

### Translation Exposure Hedging

**Net Investment Hedging:**
- **Objective**: Hedge foreign subsidiary's net investment
- **Instruments**: Forward contracts, foreign currency debt
- **Accounting**: Hedge effectiveness testing required
- **Economic Rationale**: Protect against adverse currency movements

**Balance Sheet Hedging:**
- **Asset-Liability Matching**: Match currency composition
- **Natural Hedging**: Operations in same currency as exposures
- **Netting**: Offset exposures across subsidiaries
- **Centralization**: Centralized treasury function

### Economic Exposure Hedging

**Operating Leverage:**
- **Revenue Sensitivity**: Impact of exchange rates on revenues
- **Cost Structure**: Fixed vs. variable cost currency exposure
- **Competitive Position**: Relative exposure vs. competitors
- **Long-term Strategy**: Diversification of operations

**Financial Hedging:**
- **Portfolio Approach**: Hedge portfolio of exposures
- **Rolling Hedges**: Continuous hedge program
- **Conditional Hedging**: Hedge only adverse movements
- **Proxy Hedging**: Use liquid instruments for illiquid exposures

## Interest Rate Risk Hedging

### Duration-Based Hedging

**Duration Matching:**
- **Macaulay Duration**: Weighted average time to cash flows
- **Modified Duration**: Price sensitivity to yield changes
- **Portfolio Duration**: Weighted average of individual durations
- **Immunization**: Match duration of assets and liabilities

**Duration Gap Analysis:**
Duration Gap = D_A - (L/A) × D_L

Where:
- D_A = asset duration
- D_L = liability duration  
- L/A = liability-to-asset ratio

**Convexity Adjustment:**
ΔP/P ≈ -D × Δy + (1/2) × C × (Δy)²

Where C = convexity measure

### Derivative Instruments

**Interest Rate Swaps:**
- **Plain Vanilla**: Fixed for floating rate exchange
- **Basis Swaps**: Floating for floating (different indices)
- **Forward Rate Agreements**: Single period interest rate locks
- **Swap Spreads**: Credit and liquidity risk premiums

**Interest Rate Futures:**
- **Eurodollar Futures**: Short-term interest rate futures
- **Treasury Futures**: Government bond futures
- **Hedge Ratio**: Modified duration approach
- **Cheapest to Deliver**: Quality and timing options

**Interest Rate Options:**
- **Caps and Floors**: Interest rate insurance
- **Swaptions**: Options on interest rate swaps
- **Collars**: Combination of caps and floors
- **Volatility Trading**: Vega risk management

### Asset-Liability Management

**Bank ALM:**
- **Net Interest Income**: Sensitivity to rate changes
- **Economic Value of Equity**: Present value impact
- **Gap Analysis**: Maturity and repricing gaps
- **Simulation Models**: Monte Carlo interest rate scenarios

**Pension Fund ALM:**
- **Liability Duration**: Long-term obligation duration
- **Liability-Driven Investment**: Match liability characteristics
- **Glide Paths**: De-risking as funding status improves
- **Longevity Risk**: Mortality assumption risk

**Insurance Company ALM:**
- **Product Liabilities**: Guaranteed minimum returns
- **Spread Risk**: Asset-liability spread compression
- **Reinvestment Risk**: Proceeds reinvestment at lower rates
- **Option Risk**: Policyholder behavior options

## Equity Risk Hedging

### Index Hedging

**Beta Hedging:**
Hedge Ratio = β_portfolio × (Portfolio Value / Index Future Value)

**Minimum Variance Hedging:**
- **Regression Analysis**: Portfolio returns vs. index returns
- **Time-Varying Betas**: Rolling window or GARCH models
- **Multiple Indices**: Multi-factor hedge ratios

**Portfolio Insurance:**
- **Synthetic Put**: Dynamic hedging strategy
- **CPPI**: Constant Proportion Portfolio Insurance
- **Floor Protection**: Downside protection with upside participation
- **Gap Risk**: Risk of large overnight moves

### Individual Stock Hedging

**Options Strategies:**
- **Protective Puts**: Long stock + long put
- **Covered Calls**: Long stock + short call
- **Collars**: Long stock + short call + long put
- **Straddles/Strangles**: Volatility-based strategies

**Pairs Trading:**
- **Market Neutral**: Long undervalued, short overvalued
- **Cointegration**: Statistical relationship between stocks
- **Spread Trading**: Relative value positions
- **Risk Factors**: Sector, style, momentum factors

## Commodity Risk Hedging

### Physical Commodity Hedging

**Producers:**
- **Price Risk**: Revenue uncertainty from commodity prices
- **Basis Risk**: Local vs. exchange-traded price differences
- **Quality Risk**: Grade and delivery specifications
- **Timing Risk**: Production vs. delivery timing

**Consumers:**
- **Input Cost Risk**: Raw material price volatility
- **Inventory Risk**: Stockpile value changes
- **Processing Spreads**: Input-output price relationships
- **Transportation**: Freight and logistics costs

### Energy Risk Management

**Oil and Gas:**
- **Crack Spreads**: Refining margin hedging
- **Heat Rate**: Power generation efficiency hedging
- **Weather Derivatives**: Temperature-related demand risk
- **Storage**: Contango/backwardation arbitrage

**Power Markets:**
- **Load Forecasting**: Demand prediction uncertainty
- **Generation Planning**: Optimal dispatch hedging
- **Transmission**: Congestion and basis risk
- **Renewable**: Weather-dependent generation risk

### Agricultural Hedging

**Production Risk:**
- **Yield Risk**: Weather and disease impacts
- **Quality Risk**: Grade and protein content
- **Revenue Insurance**: Crop insurance programs
- **Diversification**: Geographic and crop diversification

**Storage and Processing:**
- **Carrying Costs**: Storage, insurance, financing
- **Convenience Yield**: Inventory holding benefits
- **Crush Spreads**: Soybean processing margins
- **Calendar Spreads**: Seasonal price patterns

## Advanced Hedging Concepts

### Tail Risk Hedging

**Tail Risk Characteristics:**
- **Fat Tails**: Higher probability of extreme events
- **Asymmetry**: Different upside vs. downside risks
- **Correlation Breakdown**: Diversification failure in crises
- **Liquidity Risk**: Market liquidity evaporation

**Hedging Strategies:**
- **Put Options**: Direct downside protection
- **VIX Calls**: Volatility spike protection
- **CDS Protection**: Credit event hedging
- **Trend Following**: Crisis alpha strategies

### Macro Hedging

**Systematic Risk:**
- **Factor Exposures**: Market, size, value, momentum
- **Regime Changes**: Bull/bear market transitions
- **Policy Risk**: Monetary and fiscal policy changes
- **Geopolitical Risk**: Political and military events

**Hedging Instruments:**
- **Index Options**: Broad market protection
- **Sector Rotation**: Defensive sector allocation
- **Currency Hedging**: Dollar strength/weakness
- **Real Assets**: Inflation protection

### Behavioral Considerations

**Hedge Effectiveness Perception:**
- **Regret Aversion**: Fear of hedging "wrong" outcomes
- **Mental Accounting**: Separate evaluation of hedge results
- **Overconfidence**: Underestimating risk and hedge needs
- **Loss Aversion**: Asymmetric reaction to gains vs. losses

**Organizational Issues:**
- **Risk Culture**: Enterprise-wide risk awareness
- **Governance**: Risk committee oversight
- **Performance Measurement**: Risk-adjusted metrics
- **Incentive Alignment**: Compensation structure impact

Effective hedging requires careful analysis of risk exposures, selection of appropriate instruments, and ongoing monitoring and adjustment of hedge positions.`
        },
        {
          id: "hedging-simulator",
          title: "Advanced Hedging Strategy Simulator",
          description: "Design and test complex hedging strategies across different risk exposures",
          type: "finance-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "hedging-strategy",
            instructions: "Build comprehensive hedging strategies for various risk exposures. Analyze hedge effectiveness and adjust strategies based on market conditions.",
            scenario: {
              company: {
                type: "Multinational Corporation",
                exposures: {
                  currency: { eur: 10000000, jpy: 500000000, gbp: 5000000 },
                  commodities: { oil: 100000, copper: 50000 },
                  interestRate: { floatingDebt: 200000000, fixedAssets: 150000000 }
                }
              },
              hedgingInstruments: [
                "Forward Contracts",
                "Options", 
                "Swaps",
                "Futures",
                "Structured Products"
              ],
              marketConditions: {
                volatility: "high",
                correlations: "increasing",
                liquidity: "moderate"
              }
            },
            hints: [
              "Identify the primary risk exposures and their materiality",
              "Consider the correlation between different risk factors",
              "Evaluate the cost-benefit trade-off of different hedging strategies",
              "Account for accounting and tax implications of hedging decisions"
            ]
          }
        }
      ],
      quiz: {
        id: "derivatives-hedging-quiz",
        title: "Derivatives and Hedging Quiz",
        description: "Test your understanding of derivatives and hedging strategies",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the main advantage of using options over forwards for hedging?",
            options: [
              "Lower cost",
              "Asymmetric payoff profile",
              "No counterparty risk",
              "Higher liquidity"
            ],
            correctAnswer: 1,
            explanation: "Options provide asymmetric payoffs, offering protection against adverse movements while retaining the ability to benefit from favorable movements, unlike forwards which lock in rates."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In duration-based interest rate hedging, what does duration matching achieve?",
            options: [
              "Eliminates all interest rate risk",
              "Immunizes against parallel yield curve shifts",
              "Maximizes portfolio returns",
              "Reduces credit risk"
            ],
            correctAnswer: 1,
            explanation: "Duration matching immunizes a portfolio against small parallel shifts in the yield curve by ensuring that asset and liability durations are matched."
          }
        ]
      }
    },
    {
      id: "credit-risk",
      title: "Credit Risk Management",
      description: "Credit risk modeling, default probability, and credit derivatives",
      order: 3,
      estimatedHours: 4.5,
      content: [
        {
          id: "credit-models",
          title: "Credit Risk Models",
          description: "Probability of default models and credit scoring",
          type: "video",
          duration: 40
        },
        {
          id: "credit-derivatives-portfolio",
          title: "Credit Derivatives and Portfolio Credit Risk",
          description: "Credit default swaps, CDOs, and portfolio credit risk management",
          type: "text",
          duration: 50,
          textContent: `# Credit Derivatives and Portfolio Credit Risk

## Credit Default Swaps (CDS)

### CDS Mechanics

**Contract Structure:**
- **Protection Buyer**: Pays premium, receives protection
- **Protection Seller**: Receives premium, pays if credit event occurs
- **Reference Entity**: Company or sovereign being protected
- **Notional Amount**: Amount of protection purchased
- **Maturity**: Term of protection (typically 1-10 years)

**Credit Events:**
- **Bankruptcy**: Legal bankruptcy or insolvency proceedings
- **Failure to Pay**: Non-payment of principal or interest
- **Restructuring**: Modification of debt terms (optional in some regions)
- **Obligation Acceleration**: Early maturity triggering
- **Repudiation/Moratorium**: Government debt rejection

**Settlement Methods:**
- **Physical Settlement**: Delivery of bonds for par value
- **Cash Settlement**: Payment of (100% - Recovery Rate) × Notional
- **Auction Settlement**: ISDA auction process for recovery determination

### CDS Valuation

**Present Value of Payments:**
PV(Premiums) = Σ S × Δt_i × DF(t_i) × P(t_i)

Where:
- S = CDS spread
- Δt_i = accrual period
- DF(t_i) = discount factor
- P(t_i) = survival probability

**Present Value of Protection:**
PV(Protection) = Σ (1-R) × [P(t_{i-1}) - P(t_i)] × DF(t_i)

Where R = recovery rate

**Fair Value Spread:**
Set PV(Premiums) = PV(Protection)

**Credit Spread Curve:**
- **Bootstrapping**: Build survival curve from CDS spreads
- **Interpolation**: Linear or cubic spline methods
- **Extrapolation**: Flat forward intensity beyond last point

### CDS Applications

**Credit Protection:**
- **Loan Portfolio**: Hedge credit exposure
- **Bond Holdings**: Protect against default
- **Counterparty Risk**: Hedge derivative exposures
- **Regulatory Capital**: Reduce capital requirements

**Trading and Arbitrage:**
- **Basis Trading**: CDS vs. bond spread differences
- **Curve Trading**: Relative value along credit curve
- **Index Trading**: CDX/iTraxx index positions
- **Capital Structure**: CDS vs. equity relationships

## Collateralized Debt Obligations (CDOs)

### CDO Structure

**Asset Pool:**
- **Corporate Bonds**: Investment grade and high yield
- **Bank Loans**: Leveraged loans and syndicated facilities
- **ABS/MBS**: Asset-backed and mortgage-backed securities
- **CDS**: Synthetic exposure via credit default swaps

**Tranching:**
- **Senior Tranches**: AAA/AA rated, first loss protection
- **Mezzanine Tranches**: A/BBB rated, intermediate risk
- **Equity Tranche**: Unrated, first loss, highest yield
- **Waterfall**: Cash flow allocation mechanism

**Types of CDOs:**
- **Cash CDOs**: Actual ownership of underlying assets
- **Synthetic CDOs**: CDS-based exposure without asset ownership
- **Hybrid CDOs**: Combination of cash and synthetic exposures
- **CDO-Squared**: CDOs backed by other CDO tranches

### CDO Valuation Models

**Single Factor Gaussian Copula:**
- **Asset Correlation**: Common factor model
- **Default Threshold**: Standard normal distribution
- **Monte Carlo**: Simulation of correlated defaults
- **Tranche Losses**: Expected loss by seniority

**Base Correlation Model:**
- **Implied Correlation**: Market-implied asset correlation
- **Tranche Mapping**: Different correlations for different detachment points
- **Interpolation**: Base correlation interpolation between strikes
- **Model Limitations**: Static correlation assumption

### CDO Risk Factors

**Credit Risk:**
- **Individual Defaults**: Single name credit events
- **Correlation Risk**: Changes in default correlation
- **Recovery Risk**: Variation in recovery rates
- **Spread Risk**: Credit spread movements

**Model Risk:**
- **Correlation Assumptions**: Gaussian copula limitations
- **Recovery Assumptions**: Fixed vs. stochastic recovery
- **Factor Loading**: Systematic risk factor exposure
- **Tail Dependencies**: Extreme correlation in crises

## Portfolio Credit Risk Models

### Threshold Models

**Merton-Type Models:**
- **Asset Value**: Log-normal asset value process
- **Default Barrier**: Default when assets < liabilities
- **Correlation Structure**: Correlated asset returns
- **Systematic Factors**: Common economic drivers

**CreditMetrics:**
- **Rating Migrations**: Transition matrices
- **Asset Value Simulation**: Monte Carlo approach
- **Correlation Estimation**: Equity-based correlations
- **Portfolio Loss**: Distribution of credit losses

### Intensity Models

**Reduced Form Approach:**
- **Hazard Rates**: Stochastic default intensities
- **Survival Copulas**: Joint survival probabilities
- **Factor Models**: Common intensity factors
- **Calibration**: Market CDS and bond data

**Doubly Stochastic Models:**
- **Conditional Independence**: Given common factors
- **Marshall-Olkin**: Shock-based default modeling
- **Archimedean Copulas**: Clayton, Gumbel, Frank copulas
- **Dynamic Modeling**: Time-varying correlations

### Credit Portfolio Optimization

**Mean-CVaR Optimization:**
- **Objective**: Maximize return subject to CVaR constraint
- **Risk Measure**: Conditional Value at Risk
- **Constraints**: Concentration limits, sector limits
- **Solution**: Linear programming formulation

**Risk Budgeting:**
- **Risk Contribution**: Marginal and component CVaR
- **Active Risk**: Tracking error vs. benchmark
- **Risk-Return Attribution**: Performance decomposition
- **Rebalancing**: Dynamic risk budget allocation

## Credit Risk Transfer

### Traditional Methods

**Loan Sales:**
- **Par/Sub-Par**: Pricing based on credit quality
- **Recourse/Non-Recourse**: Risk retention vs. transfer
- **Servicing**: Ongoing loan administration
- **Regulatory Capital**: Risk-based capital relief

**Guarantees:**
- **Third-Party Guarantees**: Government, bank, insurance
- **Credit Enhancement**: Improve borrower credit quality
- **Wrap Insurance**: Portfolio-level credit protection
- **Letters of Credit**: Payment guarantees

### Modern Credit Derivatives

**Total Return Swaps:**
- **Synthetic Ownership**: Economic exposure without ownership
- **Total Return**: All cash flows plus capital appreciation/depreciation
- **Funding Cost**: LIBOR-based floating payment
- **Applications**: Regulatory capital arbitrage

**Credit-Linked Notes (CLNs):**
- **Funded Protection**: Investor provides upfront funding
- **Embedded CDS**: Note contains credit derivative
- **Principal Protection**: Subject to credit events
- **Yield Enhancement**: Higher coupon for credit risk

**Credit Spread Options:**
- **Volatility Trading**: Credit spread volatility exposure
- **Call Options**: Benefit from spread tightening
- **Put Options**: Protection against spread widening
- **Applications**: Credit spread curve trading

## Regulatory Capital and Basel Framework

### Basel III Credit Risk

**Standardized Approach:**
- **Risk Weights**: Predetermined weights by asset class
- **External Ratings**: Rating agency-based risk weights
- **Credit Risk Mitigation**: Collateral, guarantees, netting
- **Minimum Requirements**: 8% total capital ratio

**Internal Ratings-Based (IRB):**
- **Foundation IRB**: Bank estimates PD, supervisor sets LGD and EAD
- **Advanced IRB**: Bank estimates all risk parameters
- **Risk Components**: PD, LGD, EAD, maturity adjustment
- **Capital Formula**: Basel risk weight functions

**Expected Loss vs. Unexpected Loss:**
- **Expected Loss**: Provisions and write-offs
- **Unexpected Loss**: Economic capital for tail events
- **Capital Buffer**: Protection against unexpected losses
- **Stress Testing**: Adverse scenario capital adequacy

### Economic Capital Models

**VaR-Based Capital:**
- **Confidence Level**: 99.9% typically for AA rating
- **Time Horizon**: One year holding period
- **Loss Distribution**: Portfolio credit loss simulation
- **Capital Allocation**: Risk-based capital assignment

**Expected Shortfall:**
- **Tail Risk**: Expected loss beyond VaR threshold
- **Coherent Measure**: Subadditive risk measure
- **Capital Planning**: Economic capital estimation
- **ICAAP**: Internal Capital Adequacy Assessment

**RAROC (Risk-Adjusted Return on Capital):**
- **Performance Measurement**: Risk-adjusted profitability
- **Capital Allocation**: Economic capital attribution
- **Pricing**: Risk-based loan pricing
- **Decision Making**: Risk-return optimization

## Credit Risk Management Best Practices

### Portfolio Management

**Concentration Risk:**
- **Name Concentration**: Single counterparty limits
- **Sector Concentration**: Industry diversification
- **Geographic Concentration**: Regional risk limits
- **Product Concentration**: Diversification across products

**Correlation Management:**
- **Wrong Way Risk**: Adverse correlation with counterparty
- **Right Way Risk**: Favorable correlation effects
- **Stress Testing**: Correlation increase in stress
- **Model Validation**: Backtesting correlation assumptions

### Risk Monitoring

**Early Warning Systems:**
- **Rating Migrations**: Downgrade prediction models
- **Market Signals**: Equity prices, CDS spreads
- **Financial Ratios**: Leverage, coverage, liquidity
- **Qualitative Factors**: Management, industry, competition

**Stress Testing:**
- **Scenario Analysis**: Specific adverse scenarios
- **Sensitivity Testing**: Parameter shock tests
- **Historical Scenarios**: Past crisis replication
- **Monte Carlo**: Stochastic scenario generation

Credit risk management combines quantitative modeling with qualitative judgment to assess, price, and manage credit exposures across individual counterparties and portfolios.`
        }
      ],
      quiz: {
        id: "credit-risk-quiz",
        title: "Credit Risk Management Quiz",
        description: "Test your understanding of credit risk models and management",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In a Credit Default Swap, what happens when a credit event occurs?",
            options: [
              "The premium payments stop",
              "The protection seller pays the protection buyer",
              "The contract is automatically renewed",
              "The reference entity must pay additional interest"
            ],
            correctAnswer: 1,
            explanation: "When a credit event occurs in a CDS, the protection seller must compensate the protection buyer, typically paying (100% - Recovery Rate) times the notional amount."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the primary advantage of CDO tranching?",
            options: [
              "Reduces overall portfolio risk",
              "Eliminates default risk",
              "Creates securities with different risk-return profiles",
              "Increases portfolio liquidity"
            ],
            correctAnswer: 2,
            explanation: "CDO tranching creates securities with different risk-return profiles from the same underlying asset pool, allowing investors to choose their preferred level of risk exposure."
          }
        ]
      }
    },
    {
      id: "operational-risk",
      title: "Operational Risk Management",
      description: "Operational risk frameworks, controls, and measurement",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "operational-frameworks",
          title: "Operational Risk Frameworks",
          description: "Basel operational risk requirements and best practices",
          type: "video",
          duration: 35
        },
        {
          id: "operational-risk-comprehensive",
          title: "Comprehensive Operational Risk Management",
          description: "Advanced operational risk identification, assessment, and mitigation strategies",
          type: "text",
          duration: 45,
          textContent: `# Comprehensive Operational Risk Management

## Operational Risk Definition and Categories

### Basel II/III Definition

**Operational Risk:**
"The risk of loss resulting from inadequate or failed internal processes, people and systems or from external events."

**Key Components:**
- **Internal Processes**: Business process failures, control breakdowns
- **People**: Human error, fraud, unauthorized activities
- **Systems**: IT failures, system outages, cyber attacks
- **External Events**: Natural disasters, terrorism, third-party failures

### Basel Event Types

**1. Internal Fraud:**
- **Employee Fraud**: Theft, forgery, embezzlement
- **Unauthorized Activities**: Trading beyond limits, misrepresentation
- **Examples**: Rogue trading, expense fraud, kickbacks

**2. External Fraud:**
- **Third-Party Fraud**: Check fraud, identity theft, hacking
- **System Intrusions**: Cyber attacks, data breaches
- **Examples**: Card fraud, computer hacking, social engineering

**3. Employment Practices and Workplace Safety:**
- **Discrimination**: Workplace harassment, wrongful termination
- **Safety Violations**: Workplace accidents, health code violations
- **Examples**: Sexual harassment claims, workplace injuries

**4. Clients, Products & Business Practices:**
- **Product Defects**: Design flaws, inadequate disclosure
- **Client Relations**: Fiduciary breaches, customer complaints
- **Examples**: Mis-selling, market manipulation, antitrust violations

**5. Damage to Physical Assets:**
- **Natural Disasters**: Earthquakes, floods, hurricanes
- **Human-Caused Damage**: Terrorism, vandalism, fire
- **Examples**: Building damage, equipment destruction

**6. Business Disruption and System Failures:**
- **System Outages**: Hardware/software failures, network disruptions
- **Utility Failures**: Power outages, telecommunications failures
- **Examples**: Trading system downtime, data center failures

**7. Execution, Delivery & Process Management:**
- **Transaction Processing**: Settlement failures, data entry errors
- **Process Failures**: Inadequate documentation, missed deadlines
- **Examples**: Failed transactions, regulatory reporting errors

## Risk Assessment and Measurement

### Loss Distribution Approach (LDA)

**Frequency Distribution:**
- **Poisson Distribution**: Most common for event frequency
- **Negative Binomial**: Allows for overdispersion
- **Parameters**: Annual expected frequency (λ)

**Severity Distribution:**
- **Lognormal**: Common for operational losses
- **Weibull**: Flexible shape parameter
- **Generalized Pareto**: For extreme losses (above threshold)
- **Parameters**: Scale and shape parameters

**Annual Loss Distribution:**
- **Convolution**: Combine frequency and severity distributions
- **Monte Carlo Simulation**: Generate loss scenarios
- **Capital Calculation**: 99.9% confidence level over one year
- **Expected vs. Unexpected Loss**: Provisions vs. capital

### Scenario Analysis

**Expert Judgment:**
- **Workshop Approach**: Cross-functional team assessment
- **Scenario Development**: Plausible but severe events
- **Parameter Estimation**: Frequency and severity assessment
- **Calibration**: Align with external benchmarks

**Stress Testing:**
- **Tail Events**: Low probability, high impact scenarios
- **Business Environment**: Changing risk landscape
- **Emerging Risks**: New risk types and sources
- **Model Risk**: Parameter and model uncertainty

### Key Risk Indicators (KRIs)

**Leading Indicators:**
- **Staff Turnover**: High turnover increases operational risk
- **System Availability**: IT uptime and performance metrics
- **Training Hours**: Staff competency and knowledge
- **Control Self-Assessment**: Control effectiveness ratings

**Lagging Indicators:**
- **Loss Events**: Actual operational losses
- **Audit Findings**: Control deficiencies identified
- **Regulatory Actions**: Fines, penalties, sanctions
- **Customer Complaints**: Service quality issues

**Threshold Management:**
- **Red/Amber/Green**: Traffic light risk indicators
- **Escalation Procedures**: Management notification triggers
- **Action Plans**: Risk mitigation responses
- **Monitoring Frequency**: Regular KRI reporting

## Risk Controls and Mitigation

### Internal Controls Framework

**Three Lines of Defense:**

**First Line: Business Units**
- **Risk Ownership**: Business line responsibility
- **Control Implementation**: Day-to-day risk management
- **Self-Assessment**: Control effectiveness evaluation
- **Issue Identification**: Early problem detection

**Second Line: Risk Management**
- **Policy Development**: Risk management frameworks
- **Monitoring and Oversight**: Independent risk assessment
- **Risk Reporting**: Management information and metrics
- **Advisory Support**: Risk management guidance

**Third Line: Internal Audit**
- **Independent Assurance**: Objective control evaluation
- **Control Testing**: Verification of control effectiveness
- **Audit Reporting**: Findings and recommendations
- **Follow-up**: Remediation tracking

### Control Types

**Preventive Controls:**
- **Authorization Limits**: Spending and transaction limits
- **Segregation of Duties**: Separation of conflicting responsibilities
- **System Access Controls**: User authentication and permissions
- **Training Programs**: Staff competency development

**Detective Controls:**
- **Reconciliations**: Daily operational reconciliations
- **Exception Reports**: Unusual activity identification
- **Monitoring Systems**: Real-time surveillance
- **Management Reviews**: Periodic control assessments

**Corrective Controls:**
- **Incident Response**: Problem resolution procedures
- **Error Correction**: Mistake rectification processes
- **Disciplinary Actions**: Policy violation consequences
- **Process Improvements**: Control enhancement initiatives

### Technology Controls

**Information Security:**
- **Access Management**: User provisioning and deprovisioning
- **Data Encryption**: Protection of sensitive information
- **Network Security**: Firewalls and intrusion detection
- **Vulnerability Management**: Security patch management

**System Development:**
- **Change Management**: Controlled system modifications
- **Testing Procedures**: User acceptance and system testing
- **Development Standards**: Coding and documentation standards
- **Backup and Recovery**: Data protection and disaster recovery

**Data Management:**
- **Data Quality**: Accuracy, completeness, timeliness
- **Data Governance**: Ownership, stewardship, lineage
- **Data Privacy**: Personal information protection
- **Retention Policies**: Data lifecycle management

## Operational Risk Culture

### Risk Awareness

**Training and Education:**
- **Risk Awareness Programs**: General risk education
- **Role-Specific Training**: Job-relevant risk topics
- **New Hire Orientation**: Risk culture introduction
- **Continuing Education**: Ongoing skill development

**Communication:**
- **Risk Messaging**: Top-down risk communications
- **Incident Sharing**: Learning from operational events
- **Best Practices**: Knowledge sharing across units
- **Feedback Mechanisms**: Bottom-up risk reporting

### Performance Management

**Risk Accountability:**
- **Role Definitions**: Clear risk responsibilities
- **Performance Metrics**: Risk-based performance measures
- **Incentive Alignment**: Risk-adjusted compensation
- **Consequences**: Accountability for risk failures

**Reporting Culture:**
- **No-Blame Environment**: Encourage error reporting
- **Whistleblower Protection**: Safe reporting channels
- **Investigation Procedures**: Fair and thorough reviews
- **Lessons Learned**: Continuous improvement culture

## Business Continuity and Crisis Management

### Business Continuity Planning

**Risk Assessment:**
- **Business Impact Analysis**: Critical process identification
- **Recovery Time Objectives**: Maximum acceptable downtime
- **Recovery Point Objectives**: Maximum acceptable data loss
- **Dependency Mapping**: Process and system interdependencies

**Continuity Strategies:**
- **Alternative Sites**: Backup facilities and locations
- **Technology Recovery**: IT disaster recovery capabilities
- **Supplier Arrangements**: Third-party contingency plans
- **Staff Planning**: Key personnel backup and cross-training

### Crisis Management

**Crisis Response:**
- **Crisis Team**: Pre-designated response leadership
- **Communication Plans**: Internal and external messaging
- **Decision Authorities**: Clear decision-making hierarchy
- **Resource Mobilization**: Emergency funding and support

**Recovery Management:**
- **Damage Assessment**: Impact evaluation and prioritization
- **Recovery Execution**: Step-by-step restoration process
- **Stakeholder Management**: Customer, regulator, media relations
- **Lessons Learned**: Post-crisis improvement initiatives

## Regulatory Requirements

### Basel III Operational Risk

**Standardized Approach:**
- **Business Indicator**: Gross income-based calculation
- **Risk Coefficients**: Marginal coefficients by income level
- **Historical Loss Component**: Bank-specific loss experience
- **Minimum Requirements**: Data collection and controls

**Internal Models Approach:**
- **Advanced Measurement Approach**: Internal model development
- **Use Test**: Integration in business management
- **Validation Requirements**: Model accuracy and robustness
- **Supervisory Approval**: Regulatory model approval process

### Supervisory Expectations

**Risk Management Framework:**
- **Board Oversight**: Senior management responsibility
- **Risk Appetite**: Operational risk tolerance definition
- **Policies and Procedures**: Comprehensive risk framework
- **Independent Validation**: Second line oversight

**Data and Systems:**
- **Loss Data Collection**: Comprehensive loss databases
- **Risk Reporting**: Regular management reporting
- **System Requirements**: Robust operational systems
- **Documentation**: Adequate risk documentation

## Emerging Operational Risks

### Cyber Risk

**Threat Landscape:**
- **External Attackers**: Hackers, nation-states, criminals
- **Internal Threats**: Malicious or negligent employees
- **Attack Vectors**: Phishing, malware, social engineering
- **Target Assets**: Data, systems, reputation, funds

**Risk Management:**
- **Cyber Security Framework**: NIST, ISO 27001 standards
- **Threat Intelligence**: External threat monitoring
- **Incident Response**: Cyber incident management
- **Recovery Planning**: Cyber resilience and recovery

### Third-Party Risk

**Vendor Management:**
- **Due Diligence**: Vendor assessment and selection
- **Contract Management**: Risk allocation and controls
- **Ongoing Monitoring**: Vendor performance oversight
- **Exit Planning**: Vendor termination procedures

**Concentration Risk:**
- **Critical Vendors**: Key supplier identification
- **Geographic Concentration**: Vendor location risk
- **Service Concentration**: Single points of failure
- **Regulatory Expectations**: Third-party risk guidance

### Model Risk

**Model Governance:**
- **Model Inventory**: Comprehensive model catalog
- **Model Validation**: Independent model review
- **Model Risk Assessment**: Risk and materiality evaluation
- **Model Documentation**: Adequate model documentation

**Artificial Intelligence/Machine Learning:**
- **Algorithm Bias**: Discrimination and fairness issues
- **Explainability**: Model transparency requirements
- **Data Quality**: Training data adequacy and quality
- **Performance Monitoring**: Model drift and degradation

Operational risk management requires a comprehensive approach combining quantitative measurement, qualitative assessment, and strong risk culture to protect against the diverse range of operational threats facing modern financial institutions.`
        }
      ],
      quiz: {
        id: "operational-risk-quiz",
        title: "Operational Risk Management Quiz",
        description: "Test your understanding of operational risk frameworks and controls",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Basel III, operational risk is defined as the risk of loss from:",
            options: [
              "Market price movements",
              "Credit defaults",
              "Inadequate or failed internal processes, people, systems, or external events",
              "Interest rate changes"
            ],
            correctAnswer: 2,
            explanation: "Basel III defines operational risk as the risk of loss resulting from inadequate or failed internal processes, people and systems or from external events."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the Three Lines of Defense model, who owns the operational risk?",
            options: [
              "Risk Management (Second Line)",
              "Internal Audit (Third Line)",
              "Business Units (First Line)",
              "External Auditors"
            ],
            correctAnswer: 2,
            explanation: "In the Three Lines of Defense model, business units (First Line) own the operational risk and are responsible for day-to-day risk management and control implementation."
          }
        ]
      }
    },
    {
      id: "regulatory-risk",
      title: "Regulatory Risk Management",
      description: "Basel III, stress testing, and regulatory capital requirements",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "basel-requirements",
          title: "Basel III and Capital Requirements",
          description: "Understanding regulatory capital and stress testing",
          type: "video",
          duration: 35
        },
        {
          id: "regulatory-comprehensive",
          title: "Comprehensive Regulatory Risk Framework",
          description: "Advanced regulatory compliance, stress testing, and capital management",
          type: "text",
          duration: 50,
          textContent: `# Comprehensive Regulatory Risk Framework

## Basel III Capital Framework

### Capital Components

**Common Equity Tier 1 (CET1):**
- **Common Stock**: Ordinary shares issued and fully paid
- **Retained Earnings**: Accumulated profits not distributed
- **Accumulated Other Comprehensive Income (AOCI)**: Unrealized gains/losses
- **Regulatory Adjustments**: Deductions for goodwill, deferred tax assets, etc.

**Additional Tier 1 (AT1):**
- **Contingent Convertible Bonds**: CoCos that convert to equity in stress
- **Perpetual Preferred Stock**: Non-cumulative perpetual instruments
- **Requirements**: Loss absorption, going-concern triggers
- **Limit**: Cannot exceed 1.5% of risk-weighted assets

**Tier 2 Capital:**
- **Subordinated Debt**: Qualifying subordinated instruments
- **General Loan Loss Reserves**: Up to 1.25% of credit RWA
- **Maturity Requirements**: Original maturity ≥ 5 years
- **Limit**: Cannot exceed Tier 1 capital

### Capital Ratios

**Minimum Requirements:**
- **CET1 Ratio**: 4.5% of risk-weighted assets
- **Tier 1 Ratio**: 6.0% of risk-weighted assets
- **Total Capital Ratio**: 8.0% of risk-weighted assets
- **Leverage Ratio**: 3.0% of total exposure

**Capital Conservation Buffer:**
- **Buffer Requirement**: 2.5% CET1 above minimum
- **Constraint Mechanism**: Restrictions on distributions and bonuses
- **Total CET1 Requirement**: 7.0% including buffer

**Countercyclical Capital Buffer:**
- **Range**: 0-2.5% of RWA, set by national authorities
- **Economic Cycle**: Higher during credit expansion
- **International Exposures**: Weighted average of jurisdictional buffers
- **Implementation**: 12-month notice period for increases

### G-SIB Surcharge

**Systemic Importance Indicators:**
- **Size**: Total exposures (20%)
- **Interconnectedness**: Intra-financial system assets/liabilities (20%)
- **Substitutability**: Payments activity, underwriting, trading (20%)
- **Complexity**: Notional derivatives, trading securities (20%)
- **Cross-Jurisdictional Activity**: Cross-border claims (20%)

**Surcharge Buckets:**
- **Bucket 1**: 1.0% additional CET1
- **Bucket 2**: 1.5% additional CET1
- **Bucket 3**: 2.0% additional CET1
- **Bucket 4**: 2.5% additional CET1
- **Bucket 5**: 3.5% additional CET1 (highest systemic impact)

## Risk-Weighted Assets

### Credit Risk RWA

**Standardized Approach:**
- **Sovereign Exposures**: 0-150% based on external ratings
- **Bank Exposures**: 20-150% based on ratings and approach
- **Corporate Exposures**: 20-150% based on external ratings
- **Retail Exposures**: 75% risk weight
- **Real Estate**: 35% (residential) to 100% (commercial)

**Internal Ratings-Based Approach:**
- **Foundation IRB**: Banks estimate PD, supervisors set LGD/EAD
- **Advanced IRB**: Banks estimate all risk parameters
- **Risk Weight Function**: Basel formulas for different asset classes
- **Floor**: Output floor at 72.5% of standardized approach

### Market Risk RWA

**Fundamental Review of Trading Book (FRTB):**
- **Trading Book Boundary**: Clear criteria for trading vs. banking book
- **Internal Models Approach**: VaR and stressed VaR
- **Standardized Approach**: Sensitivities-based method
- **Default Risk Charge**: Jump-to-default risk for traded positions

**Standardized Approach Components:**
- **Delta Risk**: Sensitivity to underlying risk factors
- **Vega Risk**: Volatility risk from options
- **Curvature Risk**: Gamma and higher-order risks
- **Default Risk**: Jump-to-default scenarios

### Operational Risk RWA

**Standardized Approach (SA-OpRisk):**
- **Business Indicator**: Services component + Financial component + Trading component
- **Marginal Coefficients**: 12%, 15%, 18% based on BI size
- **Internal Loss Multiplier**: Bank-specific loss experience adjustment
- **Minimum**: Floor based on standardized approach

## Stress Testing

### Supervisory Stress Tests

**CCAR/DFAST (US):**
- **Scenarios**: Baseline, adverse, severely adverse
- **Horizon**: 9-quarter projection period
- **Variables**: GDP, unemployment, house prices, equity indices
- **Capital Actions**: Dividends, share repurchases, issuances

**EBA Stress Test (Europe):**
- **Frequency**: Biennial EU-wide exercise
- **Scope**: Largest EU banks by assets
- **Scenarios**: Baseline and adverse scenarios
- **Methodology**: Bottom-up bank projections

**APRA Stress Test (Australia):**
- **ADI Stress Test**: Annual domestic systemically important banks
- **Scenarios**: Housing market correction, economic recession
- **Focus**: Mortgage portfolios and housing exposures

### Internal Stress Testing

**ICAAP/ILAAP:**
- **Internal Capital Adequacy Assessment Process**: Bank's own risk assessment
- **Internal Liquidity Adequacy Assessment Process**: Liquidity risk management
- **Integration**: Business strategy and capital planning
- **Validation**: Independent review and challenge

**Model Development:**
- **Satellite Models**: Revenue, expense, loss rate projections
- **Macroeconomic Models**: Link scenarios to bank performance
- **Expert Judgment**: Qualitative overlays and adjustments
- **Validation**: Backtesting and benchmarking

### Scenario Design

**Macroeconomic Scenarios:**
- **GDP Growth**: Real economic growth rates
- **Unemployment**: Labor market stress indicators
- **Interest Rates**: Yield curve and policy rate changes
- **Asset Prices**: Equity, real estate, commodity prices
- **Exchange Rates**: Currency appreciation/depreciation

**Idiosyncratic Scenarios:**
- **Credit Events**: Large counterparty defaults
- **Operational Losses**: Cyber attacks, legal settlements
- **Market Disruptions**: Liquidity crises, volatility spikes
- **Regulatory Changes**: New capital requirements

## Liquidity Risk Management

### Liquidity Coverage Ratio (LCR)

**Formula:**
LCR = Stock of High-Quality Liquid Assets / Total Net Cash Outflows over 30 days ≥ 100%

**High-Quality Liquid Assets (HQLA):**
- **Level 1**: Central bank reserves, government securities (no haircut)
- **Level 2A**: Government-related entities, covered bonds (15% haircut)
- **Level 2B**: Corporate bonds, equities, residential mortgages (25-50% haircuts)
- **Concentration Limits**: Level 2 ≤ 40% of HQLA, Level 2B ≤ 15% of HQLA

**Cash Outflows:**
- **Retail Deposits**: 3-10% depending on stability and insurance
- **Wholesale Funding**: 20-100% based on counterparty and maturity
- **Secured Funding**: 0-25% based on asset quality
- **Derivatives and Other**: Mark-to-market, collateral needs

**Cash Inflows (Capped at 75% of outflows):**
- **Loans Maturing**: 50-100% depending on counterparty
- **Securities Maturing**: Based on liquidity and haircuts
- **Operational Inflows**: Minimum operational requirements

### Net Stable Funding Ratio (NSFR)

**Formula:**
NSFR = Available Amount of Stable Funding / Required Amount of Stable Funding ≥ 100%

**Available Stable Funding (ASF):**
- **Capital and Long-term Liabilities**: 100% ASF factor
- **Stable Retail Deposits**: 95% ASF factor
- **Less Stable Retail/SME Deposits**: 90% ASF factor
- **Wholesale Funding >1 year**: 50-100% depending on counterparty

**Required Stable Funding (RSF):**
- **Cash and Short-term Assets**: 0-5% RSF factor
- **Government Securities**: 5% RSF factor
- **Corporate/Bank Bonds**: 15-50% RSF factor
- **Loans to Retail/SME**: 65-85% RSF factor
- **Other Assets**: 85-100% RSF factor

## Large Exposures Framework

### Single Counterparty Limits

**General Limit:**
- **25% of Tier 1 Capital**: Maximum exposure to single counterparty
- **15% for G-SIBs**: Lower limit for global systemically important banks
- **Connected Counterparties**: Aggregation of exposures to connected entities

**Exemptions and Modifications:**
- **Sovereigns**: National discretion for domestic sovereign exposures
- **Covered Bonds**: 500 million EUR exemption, then risk weight adjustment
- **Trade Finance**: Short-term trade finance exemptions
- **Intragroup**: Supervisory discretion for group entities

### Calculation Methodology

**Exposure Calculation:**
- **On-Balance Sheet**: Full amount of direct exposures
- **Off-Balance Sheet**: Credit conversion factors applied
- **Derivatives**: Current exposure + potential future exposure
- **Securities Financing**: Comprehensive approach for repos/securities lending

**Credit Risk Mitigation:**
- **Collateral**: Substitute risk weight of collateral for exposure
- **Guarantees**: Substitute risk weight of guarantor
- **Credit Derivatives**: Recognition of credit protection
- **Netting**: Master netting agreements for derivatives

## Recovery and Resolution

### Recovery Planning

**Recovery Triggers:**
- **Capital Triggers**: CET1, Tier 1, Total capital thresholds
- **Liquidity Triggers**: LCR, NSFR, funding ratios
- **Market-Based Triggers**: CDS spreads, credit ratings, stock price
- **Supervisory Triggers**: Regulatory actions, stress test failures

**Recovery Options:**
- **Capital Actions**: Dividend cuts, share issuances, CoCo conversions
- **Liquidity Actions**: Asset sales, funding diversification
- **Strategic Actions**: Business line sales, geographic exit
- **Operational Actions**: Cost reduction, balance sheet optimization

### Resolution Planning

**Total Loss-Absorbing Capacity (TLAC):**
- **G-SIB Requirement**: 16% of RWA (18% from 2022)
- **Leverage Ratio**: 6% of total exposure (6.75% from 2022)
- **Eligible Instruments**: CET1, AT1, Tier 2, senior debt
- **Subordination**: Junior to excluded deposits and liabilities

**Resolution Strategies:**
- **Single Point of Entry**: Resolve at holding company level
- **Multiple Point of Entry**: Resolve individual legal entities
- **Cross-Border Coordination**: Home-host cooperation
- **Critical Functions**: Maintain essential services

### Bail-in Framework

**Bail-in Hierarchy:**
1. **CET1 Capital**: First to absorb losses
2. **AT1 Capital**: Additional Tier 1 instruments
3. **Tier 2 Capital**: Subordinated debt
4. **Senior Debt**: Eligible senior liabilities
5. **Deposits >100k EUR**: Uninsured deposits

**Excluded Liabilities:**
- **Insured Deposits**: Deposit insurance covered amounts
- **Secured Liabilities**: Covered bonds, repos with margin
- **Short-term Liabilities**: <7 days original maturity
- **Inter-bank Liabilities**: <7 days to credit institutions

Regulatory risk management requires comprehensive understanding of evolving regulatory requirements and proactive compliance management to ensure financial stability and regulatory approval for business activities.`
        }
      ],
      quiz: {
        id: "regulatory-risk-quiz",
        title: "Regulatory Risk Management Quiz",
        description: "Test your understanding of regulatory frameworks and compliance",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the minimum Common Equity Tier 1 (CET1) ratio under Basel III?",
            options: [
              "3.0%",
              "4.5%",
              "6.0%",
              "8.0%"
            ],
            correctAnswer: 1,
            explanation: "The minimum CET1 ratio under Basel III is 4.5% of risk-weighted assets, plus a 2.5% capital conservation buffer for a total of 7.0%."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What does the Liquidity Coverage Ratio (LCR) measure?",
            options: [
              "Long-term funding stability",
              "Short-term liquidity resilience over 30 days",
              "Credit risk exposure",
              "Market risk tolerance"
            ],
            correctAnswer: 1,
            explanation: "The LCR measures a bank's ability to survive a stressed funding scenario for 30 days by comparing high-quality liquid assets to net cash outflows."
          }
        ]
      }
    },

    {
      id: "integrated-risk",
      title: "Integrated Risk Management",
      description: "Enterprise risk management, risk appetite, and risk culture",
      order: 6,
      estimatedHours: 3.5,
      content: [
        {
          id: "enterprise-risk",
          title: "Enterprise Risk Management Framework",
          description: "Holistic approach to organizational risk management",
          type: "video",
          duration: 40
        },
        {
          id: "risk-culture-governance",
          title: "Risk Culture and Governance",
          description: "Building effective risk culture and governance structures",
          type: "text",
          duration: 35,
          textContent: `# Risk Culture and Governance

## Risk Culture Fundamentals

### Defining Risk Culture

**Risk Culture Components:**
- **Values and Beliefs**: Organization's fundamental risk philosophy
- **Norms and Behaviors**: How risk decisions are actually made
- **Artifacts and Symbols**: Visible manifestations of risk priorities
- **Assumptions**: Underlying beliefs about risk and risk management

**Cultural Dimensions:**
- **Risk Awareness**: Understanding of risks and their implications
- **Risk Accountability**: Ownership and responsibility for risk outcomes
- **Risk Communication**: Open and honest risk dialogue
- **Risk Decision-Making**: Integration of risk in business decisions

### Risk Appetite and Tolerance

**Risk Appetite Framework:**
- **Risk Appetite Statement**: High-level risk principles and limits
- **Risk Limits**: Quantitative boundaries for specific risks
- **Risk Tolerances**: Acceptable variation around risk appetite
- **Risk Capacity**: Maximum risk the organization can bear

**Implementation:**
- **Cascading**: From board level to business units
- **Monitoring**: Regular assessment against appetite
- **Reporting**: Escalation of appetite breaches
- **Review**: Periodic reassessment of appropriateness

## Risk Governance Structure

### Board of Directors

**Risk Oversight Responsibilities:**
- **Risk Appetite**: Approve overall risk appetite and strategy
- **Risk Culture**: Set tone at the top for risk management
- **Risk Policies**: Approve major risk policies and frameworks
- **Risk Performance**: Monitor risk performance and outcomes

**Risk Committee:**
- **Charter**: Formal mandate for risk oversight
- **Composition**: Independent directors with risk expertise
- **Reporting**: Regular reports from CRO and risk management
- **Decision Authority**: Risk limit approvals and policy changes

### Senior Management

**Chief Risk Officer (CRO):**
- **Independence**: Direct reporting to CEO and Board Risk Committee
- **Authority**: Veto power over risk-taking decisions
- **Responsibilities**: Enterprise risk strategy and implementation
- **Qualifications**: Appropriate experience and stature

**Risk Management Function:**
- **Second Line of Defense**: Independent risk oversight
- **Risk Identification**: Emerging risk assessment and monitoring
- **Risk Measurement**: Risk metrics and models development
- **Risk Reporting**: Management information and dashboards

### Business Line Management

**First Line Responsibilities:**
- **Risk Ownership**: Primary accountability for risk outcomes
- **Risk Controls**: Implementation and maintenance of controls
- **Risk Reporting**: Accurate and timely risk information
- **Risk Culture**: Embedding risk awareness in daily activities

**Risk Champions:**
- **Local Expertise**: Business-specific risk knowledge
- **Coordination**: Interface with central risk management
- **Training**: Risk education and awareness programs
- **Escalation**: Early warning of risk issues

Strong risk culture and governance provide the foundation for effective enterprise risk management and sustainable business performance.`
        }
      ],
      quiz: {
        id: "integrated-risk-quiz",
        title: "Integrated Risk Management Quiz",
        description: "Test your understanding of enterprise risk management and governance",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary role of the Chief Risk Officer (CRO)?",
            options: [
              "Generate profits for the organization",
              "Provide independent risk oversight and challenge",
              "Eliminate all risks from the business",
              "Report only to business line management"
            ],
            correctAnswer: 1,
            explanation: "The CRO's primary role is to provide independent risk oversight and challenge business decisions from a risk perspective, typically reporting to both the CEO and Board Risk Committee."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the three lines of defense model, who comprises the first line?",
            options: [
              "Internal Audit",
              "Risk Management",
              "Business Units",
              "External Auditors"
            ],
            correctAnswer: 2,
            explanation: "Business units comprise the first line of defense and have primary responsibility for risk management, including identifying, assessing, and controlling risks in their operations."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "risk-management-project",
      title: "Enterprise Risk Management Project",
      description: "Develop comprehensive risk management framework for a financial institution with stress testing and scenario analysis",
      type: "project",
      dueWeeks: 7,
      maxScore: 100,
      instructions: `# Enterprise Risk Management Project

## Objective
Develop a comprehensive risk management framework for a financial institution including risk measurement, monitoring, and mitigation strategies.

## Requirements
1. **Risk Identification**: Identify all major risk categories and exposures
2. **Risk Measurement**: Implement VaR and stress testing methodologies
3. **Risk Monitoring**: Design risk dashboards and reporting systems
4. **Risk Mitigation**: Develop hedging and risk reduction strategies
5. **Regulatory Compliance**: Address Basel III and other requirements
6. **Crisis Management**: Design stress scenarios and response plans

## Deliverables
- Risk management policy document
- Technical implementation guide (15-20 pages)
- Risk measurement models and calculations
- Stress testing scenarios and results
- Risk dashboard mockups and reporting templates`,
      submissionFormat: "pdf",
      resources: [
        "Risk management frameworks and templates",
        "Regulatory guidance documents",
        "Case studies in risk management"
      ]
    }
  ],

  finalExam: {
    id: "risk-management-final",
    title: "Risk Management and Derivatives Final Exam",
    description: "Comprehensive examination of risk management concepts and derivatives applications",
    timeLimit: 120,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What is the main limitation of Value at Risk (VaR) as a risk measure?",
        options: [
          "It doesn't account for portfolio diversification",
          "It doesn't measure tail risk beyond the confidence level",
          "It cannot be calculated for complex instruments",
          "It doesn't consider time horizon effects"
        ],
        correctAnswer: 1,
        explanation: "VaR's main limitation is that it doesn't measure tail risk - the potential losses beyond the VaR confidence level. Expected Shortfall addresses this limitation."
      }
    ]
  },

  quizzes: [
    {
      id: "comprehensive-risk-quiz",
      title: "Comprehensive Risk Management Quiz",
      description: "Advanced quiz covering all aspects of risk management and derivatives",
      timeLimit: 35,
      passingScore: 85,
      attempts: 2,
      questions: [
        {
          id: "comp-q1",
          type: "multiple-choice",
          question: "Which risk measure is considered 'coherent' according to Artzner et al.?",
          options: [
            "Value at Risk (VaR)",
            "Expected Shortfall (ES)",
            "Standard Deviation",
            "Maximum Drawdown"
          ],
          correctAnswer: 1,
          explanation: "Expected Shortfall is a coherent risk measure as it satisfies all four properties: monotonicity, translation invariance, positive homogeneity, and subadditivity."
        },
        {
          id: "comp-q2",
          type: "multiple-choice",
          question: "In a CDO structure, which tranche typically receives the highest yield?",
          options: [
            "Senior tranche",
            "Mezzanine tranche",
            "Equity tranche",
            "Super senior tranche"
          ],
          correctAnswer: 2,
          explanation: "The equity tranche receives the highest yield as it bears the first losses and has the highest risk, requiring higher compensation for investors."
        },
        {
          id: "comp-q3",
          type: "multiple-choice",
          question: "What is the primary purpose of the Basel III Liquidity Coverage Ratio (LCR)?",
          options: [
            "Measure long-term funding stability",
            "Ensure short-term liquidity resilience",
            "Calculate regulatory capital requirements",
            "Assess credit risk exposures"
          ],
          correctAnswer: 1,
          explanation: "The LCR ensures banks have sufficient high-quality liquid assets to survive a stressed funding scenario for 30 days, promoting short-term liquidity resilience."
        },
        {
          id: "comp-q4",
          type: "multiple-choice",
          question: "Which Greek measures an option's sensitivity to changes in the underlying asset's volatility?",
          options: [
            "Delta",
            "Gamma",
            "Vega",
            "Theta"
          ],
          correctAnswer: 2,
          explanation: "Vega measures an option's sensitivity to changes in the implied volatility of the underlying asset, representing volatility risk."
        }
      ]
    }
  ]
};